import React, { useState, useEffect } from 'react';
import { 
  CreditCard, 
  Bitcoin, 
  Wallet2, 
  Landmark, 
  Smartphone, 
  ChevronRight, 
  ChevronDown, 
  ChevronUp,
  Loader2
} from 'lucide-react';
import { usePaymentMethods } from '../../hooks/usePaymentMethods';

interface PaymentGatewaySelectorProps {
  regionCode: string;
  onSelect: (methodId: string, gatewayId: string) => void;
  planId?: string;
}

// Map of icon names to Lucide components
const iconMap: Record<string, React.ComponentType<any>> = {
  'credit-card': CreditCard,
  'bitcoin': Bitcoin,
  'wallet': Wallet2,
  'landmark': Landmark,
  'smartphone': Smartphone,
  'paypal': Wallet2
};

export const PaymentGatewaySelector: React.FC<PaymentGatewaySelectorProps> = ({ 
  regionCode,
  onSelect,
  planId
}) => {
  const { methods, isLoading } = usePaymentMethods(regionCode);
  const [expandedGateway, setExpandedGateway] = useState<string | null>(null);
  
  // Automatically expand the first gateway if none is expanded
  useEffect(() => {
    if (methods.length > 0 && !expandedGateway) {
      setExpandedGateway(methods[0].id);
    }
  }, [methods, expandedGateway]);

  // Automatically expand the first gateway if none is expanded
  useEffect(() => {
    if (methods.length > 0 && !expandedGateway) {
      setExpandedGateway(methods[0].id);
    }
  }, [methods, expandedGateway]);

  const toggleGateway = (gatewayId: string) => {
    setExpandedGateway(expandedGateway === gatewayId ? null : gatewayId);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (methods.length === 0) {
    return (
      <div className="text-center py-6">
        <p className="text-gray-500 dark:text-gray-400">
          No payment methods available for your region.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
        Select Payment Method
      </h2>
      
      <div className="space-y-4">
        {methods.map((gateway) => (
          <div key={gateway.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            {/* Only show gateways that have visible methods */}
            {gateway.methods.filter(method => {
              if (method.metadata?.useDirectPayment === true) return true;
              if (method.metadata?.useDirectLink === true) {
                const urls = method.metadata?.subscriptionUrls || {};
                if (!planId || urls[planId]) return true;
                return false;
              }
              const planIds = method.metadata?.planIds || {};
              if (!planId || planIds[planId]) return true;
              return false;
            }).length === 0 ? null : (
              <>
            {/* Gateway Header */}
            <button
              onClick={() => toggleGateway(gateway.id)}
              className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <span className="font-medium text-gray-900 dark:text-white">
                {gateway.name}
              </span>
              {expandedGateway === gateway.id ? (
                <ChevronUp className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              )}
            </button>
            
            {/* Payment Methods */}
            {expandedGateway === gateway.id && gateway.methods.length > 0 && (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {gateway.methods.map((method) => {
                  // Skip methods that require plan IDs if we don't have one
                  if (method.metadata?.useDirectPayment === true) {
                    // Direct payment methods are always shown
                  } else if (method.metadata?.useDirectLink === true) {
                    // For direct link methods, check if we have a URL for this plan
                    const urls = method.metadata?.subscriptionUrls || {};
                    if (planId && !urls[planId]) {
                      return null; // Skip if no URL for this plan
                    }
                  } else {
                    // For backend methods, check if we have a plan ID for this plan
                    const planIds = method.metadata?.planIds || {};
                    if (planId && !planIds[planId]) {
                      return null; // Skip if no plan ID for this plan
                    }
                  }
                  
                  const IconComponent = iconMap[method.icon] || CreditCard;
                  
                  return (
                    <button
                      key={method.id}
                      onClick={() => onSelect(method.id, gateway.id)}
                      className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group"
                    >
                      <div className="flex items-center">
                        <div className="flex-shrink-0 w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center group-hover:bg-blue-500 transition-colors">
                          <IconComponent className="w-5 h-5 text-blue-600 dark:text-blue-400 group-hover:text-white" />
                        </div>
                        <div className="ml-4 text-left">
                          <h3 className="font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400">
                            {method.name}
                          </h3>
                          {method.description && (
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {method.description}
                            </p>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-500 dark:group-hover:text-blue-400" />
                    </button>
                  );
                })}
              </div>
            )}
            </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};